import React, { useEffect } from 'react';
import '../styles/TermsPage.css';

/**
 * Terms & Conditions — content is reproduced verbatim from the text supplied
 * by VIN WHOLESALE. Do not edit the wording.
 */
const TermsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-page">
      <h1 className="terms-page__title">VIN WHOLESALE TERMS &amp; CONDITIONS</h1>
      <p className="terms-page__subtitle">Business-to-Business Sales Only</p>

      <p>
        VIN WHOLESALE (“VIN”, “VIN WHOLESALE”, “we”, “our”, or “us”) operates as a wholesale
        distributor and sells products exclusively to licensed businesses.
      </p>
      <p>
        By creating an account, placing an order, accepting delivery, or conducting business with
        VIN WHOLESALE, the Customer agrees to be bound by these Terms and Conditions.
      </p>
      <p>
        VIN WHOLESALE reserves the right to refuse service, cancel orders, suspend accounts, limit
        quantities, or terminate business relationships at its sole discretion.
      </p>

      <hr />

      <section className="terms-page__section">
        <h2>Customer Eligibility</h2>
        <p>Customers must:</p>
        <ul>
          <li>Be at least twenty-one (21) years of age.</li>
          <li>Hold a valid business license and any required state or local permits.</li>
          <li>Provide accurate business information.</li>
          <li>Maintain compliance with all applicable federal, state, and local laws.</li>
        </ul>
        <p>
          VIN WHOLESALE reserves the right to request business licenses, tobacco permits, resale
          certificates, identification, or additional documentation at any time.
        </p>
        <p>
          VIN WHOLESALE may suspend or terminate any account that fails verification requirements.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Compliance Responsibility</h2>
        <p>
          The Customer is solely responsible for determining whether products purchased from VIN
          WHOLESALE may legally be possessed, distributed, marketed, transported, or sold within the
          Customer’s jurisdiction.
        </p>
        <p>
          VIN WHOLESALE makes no representation that any product is legal for sale in every city,
          county, state, or jurisdiction.
        </p>
        <p>The Customer assumes full responsibility for:</p>
        <ul>
          <li>Regulatory compliance</li>
          <li>Product registration requirements</li>
          <li>Licensing requirements</li>
          <li>Tax obligations</li>
          <li>Age-verification compliance</li>
          <li>Retail sales compliance</li>
        </ul>
        <p>
          VIN WHOLESALE shall not be responsible for losses resulting from changes in laws,
          regulations, enforcement actions, product restrictions, or product bans.
        </p>
        <p>
          Products purchased in compliance at the time of sale remain non-returnable due to future
          legal or regulatory changes.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Taxes</h2>
        <p>
          Customers are responsible for all applicable federal, state, local, excise, tobacco,
          nicotine, hemp, vapor, sales, use, and other taxes associated with their purchases unless
          otherwise required by law.
        </p>
        <p>
          Customers must provide valid resale certificates or tax-exempt documentation when
          applicable.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Product Disclaimers</h2>
        <p>
          Certain products sold by VIN WHOLESALE may contain nicotine, tobacco, hemp-derived
          ingredients, kratom, caffeine, botanical extracts, or other regulated substances.
        </p>
        <p>Customers acknowledge and agree that:</p>
        <ul>
          <li>Products are intended only for lawful sale and distribution.</li>
          <li>Products are not intended for use by minors.</li>
          <li>Products are not intended for pregnant or nursing women.</li>
          <li>
            Products have not necessarily been evaluated by the U.S. Food and Drug Administration
            unless expressly stated by the manufacturer.
          </li>
          <li>Products are not intended to diagnose, treat, cure, or prevent any disease.</li>
        </ul>
        <p>
          VIN WHOLESALE relies upon information provided by manufacturers and makes no independent
          guarantees regarding product claims, efficacy, ingredients, or regulatory status.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Hemp-Derived Products</h2>
        <p>
          Any hemp-derived products sold by VIN WHOLESALE are represented by their manufacturers as
          compliant with applicable federal law, including the 2018 Farm Bill, where applicable.
        </p>
        <p>
          Customers are solely responsible for verifying legality within their jurisdiction before
          purchasing, transporting, distributing, or selling such products.
        </p>
        <p>
          VIN WHOLESALE assumes no responsibility for state, county, municipal, or future regulatory
          restrictions.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Kratom Products</h2>
        <p>
          Customers purchasing kratom or kratom-derived products acknowledge that laws governing such
          products vary by jurisdiction.
        </p>
        <p>
          The Customer is solely responsible for determining legality before purchasing or reselling
          any kratom-related product.
        </p>
        <p>
          VIN WHOLESALE makes no guarantee regarding the legality of kratom products in any
          jurisdiction.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Battery and Electronic Device Warning</h2>
        <p>
          Products containing rechargeable batteries, electronic components, charging systems, or
          vapor devices carry inherent risks.
        </p>
        <p>
          VIN WHOLESALE shall not be liable for damages, injuries, fires, explosions, property
          damage, misuse, modification, improper storage, improper charging, or improper handling of
          batteries or electronic products.
        </p>
        <p>Customers assume all risks associated with such products.</p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Pricing and Product Information</h2>
        <p>VIN WHOLESALE reserves the right to:</p>
        <ul>
          <li>Correct pricing errors</li>
          <li>Correct typographical errors</li>
          <li>Modify product descriptions</li>
          <li>Change product availability</li>
          <li>Limit quantities</li>
          <li>Cancel orders</li>
        </ul>
        <p>
          If an incorrect price is displayed, VIN WHOLESALE may cancel the order regardless of
          whether payment has been processed.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Shipping and Delivery</h2>
        <p>VIN WHOLESALE will make commercially reasonable efforts to ship orders promptly.</p>
        <p>Shipping dates are estimates only and are not guaranteed.</p>
        <p>
          Risk of loss transfers to the Customer upon delivery to the carrier or upon Customer
          pickup.
        </p>
        <p>
          If a Customer supplies their own carrier, freight company, broker, or Bill of Lading
          (BOL), VIN WHOLESALE shall not be responsible for loss, damage, delays, shortages, or
          delivery issues after shipment leaves our facility.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Inspection of Goods</h2>
        <p>Customers must inspect all shipments immediately upon receipt.</p>
        <p>
          Any claims for shortages, damage, incorrect items, or shipping discrepancies must be
          reported within forty-eight (48) hours of delivery.
        </p>
        <p>
          Failure to notify VIN WHOLESALE within forty-eight (48) hours constitutes acceptance of
          the shipment.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Returns and Refunds</h2>
        <p className="terms-page__final">ALL SALES ARE FINAL.</p>
        <p>
          VIN WHOLESALE does not accept returns, exchanges, or refunds except where required by law
          or approved in writing by management.
        </p>
        <p>Products may not be returned due to:</p>
        <ul>
          <li>Slow sales</li>
          <li>Customer preference</li>
          <li>Product performance</li>
          <li>Market changes</li>
          <li>Pricing changes</li>
          <li>Regulatory changes</li>
        </ul>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, VIN WHOLESALE shall not be liable for any indirect,
          incidental, consequential, special, punitive, or exemplary damages, including loss of
          profits, loss of business, loss of goodwill, loss of revenue, or business interruption.
        </p>
        <p>
          VIN WHOLESALE’s total liability for any claim shall not exceed the purchase price of the
          product giving rise to the claim.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Indemnification</h2>
        <p>
          Customer agrees to defend, indemnify, and hold harmless VIN WHOLESALE and its owners,
          officers, directors, employees, affiliates, representatives, and agents from and against
          any claims, liabilities, damages, fines, penalties, losses, expenses, or attorney fees
          arising from:
        </p>
        <ul>
          <li>Customer’s resale of products</li>
          <li>Customer’s regulatory violations</li>
          <li>Customer’s tax obligations</li>
          <li>Customer’s marketing claims</li>
          <li>Customer’s misuse of products</li>
          <li>Customer’s violation of any law or regulation</li>
        </ul>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Intellectual Property</h2>
        <p>
          All trademarks, logos, graphics, website content, product images, catalogs, pricing
          materials, and other content owned by VIN WHOLESALE remain the exclusive property of VIN
          WHOLESALE.
        </p>
        <p>
          Unauthorized reproduction, distribution, copying, or commercial use is prohibited without
          prior written permission.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Prohibited Uses</h2>
        <p>Customers may not:</p>
        <ul>
          <li>Violate any applicable law.</li>
          <li>Misrepresent products.</li>
          <li>Sell products to minors.</li>
          <li>Submit false information.</li>
          <li>Attempt to interfere with VIN WHOLESALE systems.</li>
          <li>Use VIN WHOLESALE trademarks without authorization.</li>
        </ul>
        <p>VIN WHOLESALE reserves the right to terminate accounts for any prohibited activity.</p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Governing Law</h2>
        <p>
          These Terms and Conditions shall be governed by and interpreted under the laws of the
          Commonwealth of Kentucky, without regard to conflict-of-law principles.
        </p>
        <p>
          Any dispute arising from these Terms shall be resolved exclusively in the courts located in
          Kentucky.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Modifications</h2>
        <p>
          VIN WHOLESALE reserves the right to modify these Terms and Conditions at any time without
          prior notice.
        </p>
        <p>
          Continued use of VIN WHOLESALE services following any changes constitutes acceptance of the
          revised Terms and Conditions.
        </p>
      </section>

      <p className="terms-page__updated">Last Updated: June 2026</p>
    </div>
  );
};

export default TermsPage;
